<script setup lang="ts">
  defineOptions({ name: 'CPAvatar', inheritAttrs: false })

  // Type imports
  import type { PropType } from 'vue'

  import { computed } from 'vue'

  import { HLAvatar } from '@platform-ui/highrise'

  import { generateDRMImageUrl } from '@common/media-layer/utils/image.utils'

  import { isCDNOrGCSUrl, isRelativePath } from '@common/media-layer/utils/url.utils'

  // Props definitions
  const props = defineProps({
    size: {
      type: [String, Number] as PropType<'2xl' | 'xl' | 'lg' | 'md' | 'sm' | 'xs' | '2xs' | '3xs' | number>,
      required: false,
      default: 'md',
    },
    src: {
      type: String,
      required: false,
      default: '',
    },
    fallbackSrc: {
      type: String,
      required: false,
      default: '',
    },
    title: {
      type: String,
      required: false,
      default: '',
    },
    // Shape of the avatar: 'circle' (default) or 'square'
    shape: {
      type: String as PropType<'circle' | 'square'>,
      required: false,
      default: 'circle',
    },
    source: {
      type: String as PropType<'communities' | 'courses' | 'client-portal' | 'gokollab' | string>,
      required: false,
      default: undefined,
    },
    useRawUrl: {
      type: Boolean,
      default: false,
    },
  })

  const initials = computed(() => {
    const name = props.title.trim()
    if (!name) return ''
    const parts = name.split(/\s+/)
    if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? ''
    const first = parts[0][0] ?? ''
    const last = parts[parts.length - 1][0] ?? ''
    return `${first}${last}`.toUpperCase()
  })

  // Avatar size mapping (in pixels)
  const avatarSizeMap: Record<string, number> = {
    '3xs': 24,
    '2xs': 32,
    xs: 40,
    sm: 44,
    md: 48,
    lg: 56,
    xl: 64,
    '2xl': 80,
  }

  // Generate optimized avatar URL
  const optimizedAvatarSrc = computed(() => {
    if (!props.src) return ''

    // If it's already a blob URL (during upload), use it directly
    if (props.src.startsWith('blob:') || props.useRawUrl) return props.src

    // Optimize only CDN/GCS/relative sources; keep external avatars untouched
    if (!isCDNOrGCSUrl(props.src) && !isRelativePath(props.src)) {
      return props.src
    }

    const size = typeof props.size === 'number' ? props.size : avatarSizeMap[props.size] || 48

    try {
      // Generate optimized URL with proper sizing for retina displays
      // The function will auto-detect bucket from the GCS URL
      const optimized = generateDRMImageUrl({
        src: props.src,
        width: size * 2, // 2x for retina displays
        height: size * 2, // Square aspect ratio
        quality: 85,
        format: 'webp',
        resize: 'fill', // Fill the square container
        source: props.source,
      })
      // If optimization returns empty, fall back to original
      return optimized || props.src
    } catch (error) {
      // If optimization fails, use original URL
      console.warn('Avatar optimization failed, using original URL:', error)
      return props.src
    }
  })
</script>

<template>
  <HLAvatar
    v-if="props.src"
    v-bind="$attrs"
    :size="props.size"
    :src="optimizedAvatarSrc"
    :fallback-src="props.fallbackSrc || props.src"
    :class="{ 'cp-avatar--square': props.shape === 'square' }"
  >
    <slot />
  </HLAvatar>
  <HLAvatar v-else v-bind="$attrs" :size="props.size" :class="{ 'cp-avatar--square': props.shape === 'square' }">
    <slot v-if="$slots.default" />
    <template v-else>{{ initials }}</template>
  </HLAvatar>
</template>

<style scoped>
  .cp-avatar--square {
    border-radius: 8px !important;
  }

  .cp-avatar--square :deep(img) {
    border-radius: 8px !important;
  }
</style>
